
import { mapState } from 'vuex';
import { formatCurrency } from '@/utils/currency';
import { isAllZeroes } from '@/utils/number-helpers';
import Highchart from '@/components/highchart-new';

export default {
  name: 'PlaygroundGraph',

  components: {
    Highchart,
  },

  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState({
      age: (state) => state['pension-calculator'].inputs.age,
      slider_monthly_contribution: (state) =>
        state['pension-calculator'].inputs.slider_monthly_contribution,
      slider_one_time_contribution: (state) =>
        state['pension-calculator'].inputs.slider_one_time_contribution,

      currentRetirementStatus: (state) =>
        state['pension-calculator'].outputs.expected_retirement_current_status,
      worstCurrentRetirementStatus: (state) =>
        state['pension-calculator'].outputs.worst_retirement_current_status,
      bestCurrentRetirementStatus: (state) =>
        state['pension-calculator'].outputs.best_retirement_current_status,
      pppRetirement: (state) =>
        state['pension-calculator'].outputs.expected_retirement_ppp,
      pppWorstRetirement: (state) =>
        state['pension-calculator'].outputs.worst_retirement_ppp,
      pppBestRetirement: (state) =>
        state['pension-calculator'].outputs.best_retirement_ppp,
      investmentPropertyRetirement: (state) =>
        state['pension-calculator'].outputs
          .expected_retirement_investment_property,
      investmentPropertyWorstRetirement: (state) =>
        state['pension-calculator'].outputs
          .worst_retirement_investment_property,
      investmentPropertyBestRetirement: (state) =>
        state['pension-calculator'].outputs.best_retirement_investment_property,
      ownHomeRetirement: (state) =>
        state['pension-calculator'].outputs.expected_retirement_own_home,
      additionalIncome: (state) =>
        state['pension-calculator'].outputs
          .expected_retirement_additional_income,
      // Chart
      pppData: (state) => state['pension-calculator'].chart.ppp,
      investmentPropertyData: (state) =>
        state['pension-calculator'].chart.investmentProperty,
      ownHomeData: (state) => state['pension-calculator'].chart.ownHome,
      feasibleRetirementData: (state) =>
        state['pension-calculator'].chart.feasibleRetirement,
    }),

    currentStatusText() {
      const expected = this.currentRetirementStatus;
      const worst = this.worstCurrentRetirementStatus;
      const best = this.bestCurrentRetirementStatus;
      const useShort = new Set([expected, worst, best]).size === 1;

      return useShort
        ? this.$t('currentStatusShort', { expected })
        : this.$t('currentStatus', { expected, worst, best });
    },

    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    // Graph config
    plotOptions() {
      return {
        areaspline: {
          marker: {
            enabled: false,
            fillColor: this.$colors.white.base,
            lineWidth: 2,
            radius: 4,
            symbol: 'circle',
            states: {
              hover: {
                enabled: true,
              },
            },
          },
          pointStart: this.age + 1,
        },
      };
    },
    series() {
      const feasibleRetirement = {
        color: {
          linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
          stops: [],
        },
        data: this.feasibleRetirementData,
        hover: {
          lineWidth: 2,
          fillColor: this.$colors.secondary.base,
        },
        lineColor: this.$colors.secondary.base,
        marker: {
          lineColor: this.$colors.secondary.base,
        },
        name: this.$t('feasibleRetirement'),
        zIndex: 4,
      };
      const ppp = {
        color: {
          linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
          stops: [
            [0, `${this.$colors.primary.darken1}11`], // start
            [1, `${this.$colors.primary.darken1}00`], // end
          ],
        },
        data: this.pppData,
        hover: {
          lineWidth: 2,
          fillColor: this.$colors.primary.base,
        },
        lineColor: this.$colors.primary.base,
        marker: {
          lineColor: this.$colors.primary.base,
        },
        name: this.$t('ppp'),
        zIndex: 3,
      };

      const investmentProperty = !isAllZeroes(this.investmentPropertyData)
        ? {
            color: {
              linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
              stops: [
                [0, `${this.$colors.primary.base}11`], // start
                [1, `${this.$colors.primary.base}00`], // end
              ],
            },
            data: this.investmentPropertyData,
            hover: {
              lineWidth: 2,
              fillColor: this.$colors.blue.base,
            },
            lineColor: this.$colors.blue.base,
            marker: {
              lineColor: this.$colors.blue.base,
            },
            name: this.$t('investmentProperty'),
            zIndex: 2,
          }
        : null;
      const ownHome = !isAllZeroes(this.ownHomeData)
        ? {
            color: {
              linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
              stops: [
                [0, `${this.$colors.blue.base}11`], // start
                [1, `${this.$colors.blue.base}00`], // end
              ],
            },
            data: this.ownHomeData,
            hover: {
              lineWidth: 2,
              fillColor: this.$colors.primary.lighten1,
            },
            lineColor: this.$colors.primary.lighten1,
            marker: {
              lineColor: this.$colors.primary.lighten1,
            },
            name: this.$t('ownHome'),
            zIndex: 1,
          }
        : null;

      return [feasibleRetirement, ppp, investmentProperty, ownHome].filter(
        (s) => s,
      );
    },
    tooltip() {
      return {
        distance: this.isMobile ? 60 : 30,
        formatter: ({ chart }) => {
          const plottedAge = chart.hoverPoint.x;

          const intersectionTooltip = this.getIntersectionTooltip(plottedAge);
          const pointTooltip = this.getPointTooltip(chart);

          return `
            <div class="tooltip tooltip--dark">
              <div class="tooltip__header">
                Age ${plottedAge}
              </div>

              <div class="tooltip__body">
                ${intersectionTooltip || pointTooltip}
              </div>
            </div>
          `;
        },
        shared: true,
        useHTML: true,
      };
    },
    graphConfig() {
      const { plotOptions, series, tooltip } = this;
      return {
        chart: {
          backgroundColor: this.$colors.primary.lighten5,
          spacingTop: 50,
          height: 380,
          style: {
            fontFamily: 'TTNorms',
            fontSize: 12,
          },
          type: 'areaspline',
        },
        title: undefined,
        credits: {
          enabled: false,
        },
        legend: {
          align: this.isMobile ? 'left' : 'center',
          itemStyle: {
            fontWeight: 400,
          },
          itemMarginBottom: 8,
          itemMarginTop: 8,
          itemDistance: 10,
        },
        plotOptions,
        series,
        tooltip,
        xAxis: {
          allowDecimals: false,
          title: {
            text: this.$t('xAxisTitle'),
            align: 'high',
          },
        },
        yAxis: {
          allowDecimals: true,
          labels: {
            formatter() {
              if (this.value === 0) return '0 €';
              const fixed0 = this.value.toFixed(0);

              return fixed0.length <= 6
                ? `${this.value / 1000}k €`
                : `${this.value / 1000000}M €`;
            },
          },
          title: {
            align: 'high',
            offset: 10,
            rotation: 0,
            text: this.$t('yAxisTitle'),
            y: -30,
          },
        },
      };
    },
  },

  methods: {
    getPointTooltip(chart) {
      const hasInvestmentPropertyData = !isAllZeroes(
        this.investmentPropertyData,
      );
      const hasOwnHomeData = !isAllZeroes(this.ownHomeData);

      return `
        ${this.getTooltipRow('ppp', chart)}
        ${
          hasInvestmentPropertyData
            ? this.getTooltipRow('investmentProperty', chart)
            : ''
        }
        ${hasOwnHomeData ? this.getTooltipRow('ownHome', chart) : ''}
      `;
    },
    getTooltipRow(translationKey, chart) {
      const serie = chart.series.find(
        ({ name }) => name === this.$t(translationKey),
      );
      const asset = serie.data[chart.hoverPoint.index]?.y?.toFixed(0);

      return `
        <span class="tooltip__label">
          <span class="tooltip__name">${this.$t(translationKey)}</span>
          <span class="tooltip__value font-weight-medium">
            ${formatCurrency(asset)}
          </span>
        </span>
      `;
    },
    getIntersectionTooltip(plottedAge) {
      const isPPPIntersection = plottedAge === this.pppRetirement;
      if (isPPPIntersection) return this.getIntersectionTooltipText('ppp');
      const isInvestmentIntersection =
        plottedAge === this.investmentPropertyRetirement;
      if (isInvestmentIntersection)
        return this.getIntersectionTooltipText('investmentProperty');
      const isOwnHomeIntersection = plottedAge === this.ownHomeRetirement;
      if (isOwnHomeIntersection)
        return this.getIntersectionTooltipText('ownHome');

      return '';
    },
    getIntersectionTooltipText(translationKey) {
      const expected = this[`${translationKey}Retirement`];
      const worst = this[`${translationKey}WorstRetirement`];
      const best = this[`${translationKey}BestRetirement`];

      return this.$t(`intersections.${translationKey}`, {
        expected,
        worst,
        best,
      });
    },

    setStoreProperty(property, value) {
      this.$store.commit('pension-calculator/setInputProperty', {
        property,
        value,
      });
    },
  },
};
